import { useParams } from 'react-router-dom';
import { t } from 'i18next';
import { useClient } from 'jsonapi-react';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FiPaperclip, FiSend, FiX } from 'react-icons/fi';
import { ChatFooter, ChatForm } from '@/components/custom/chat/chat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CardIcon } from '@/components/ui/card';
import { LoaderSpinner } from '@/components/custom/loader';
import clsx from 'clsx';
import { Tooltip } from 'react-tooltip';
import warning from '@/assets/images/icons/warning.svg';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import api from '@/services/api';

export default function CommentForm(props) {
  const client = useClient();
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const { commentForm, meta, setMeta } = props;
  const fileInputRef = useRef(null);
  const authorityLevel = useAuthorityLevel();
  const isStudent = authorityLevel === 'student';
  const [isSubmittingFile, setIsSubmittingFile] = useState(false);

  const [attachments, setAttachments] = useState([]);

  const videoLink =
    commentForm.values.body.match(/https?:\/\/[^\s]+/gi)?.find(url => {
      try {
        const hostname = new URL(url).hostname.toLowerCase();

        return hostname.includes('youtube.com') || hostname.includes('youtu.be') || hostname.includes('vimeo.com');
      } catch {
        return false;
      }
    }) ?? null;

  const hasVideoLink = !!videoLink;

  const handleSubmitFile = async () => {
    if (attachments.length >= 5) {
      toast.error('Você pode anexar no máximo 5 arquivos por comentário.');
      return;
    }

    if (isSubmittingFile) return;

    setIsSubmittingFile(true);

    const tempId = crypto.randomUUID();
    const controller = new AbortController();

    const attachmentPreview = {
      tempId,
      name: commentForm.values.file?.name || videoLink,
      status: 'loading',
      controller
    };

    setAttachments(prev => [...prev, attachmentPreview]);

    const formData = new FormData();

    if (commentForm.values.file) {
      formData.append('attachment_type', 'file');
      formData.append('file', commentForm.values.file);
    }

    if (hasVideoLink) {
      formData.append('attachment_type', 'video_link');
      formData.append('url', videoLink);
    }

    try {
      const { data } = await api.post('attachments', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        signal: controller.signal
      });

      setAttachments(prev =>
        prev.map(item =>
          item.tempId === tempId
            ? {
                ...item,
                id: data.attributes.id,
                name: data.attributes?.['file-name'] || data?.attributes?.url,
                status: 'success',
                controller: undefined
              }
            : item
        )
      );
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        setAttachments(prev => prev.filter(item => item.tempId !== tempId));
        return;
      }

      setAttachments(prev =>
        prev.map(item =>
          item.tempId === tempId
            ? {
                ...item,
                status: 'error',
                controller: undefined
              }
            : item
        )
      );

      toast.error('Erro ao fazer upload do arquivo!');
    } finally {
      commentForm.setFieldValue('file', null);

      if (videoLink) {
        commentForm.setFieldValue('body', commentForm.values.body.replace(videoLink, '').trim());
        setIsSubmittingFile(false);
      }
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (loading) return;
    try {
      setLoading(true);
      const { meta } = await client.mutate(`/live_classrooms/${params.liveClassId}/live_classroom_messages`, {
        body: commentForm.values.body,
        ['attachment-ids']: attachments.filter(file => file.status === 'success').map(file => file.id)
      });

      commentForm.resetForm();
      setAttachments([]);
      if (isStudent) {
        setMeta(meta);
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao criar comentário!');
    } finally {
      setLoading(false);
    }
  };

  const deleteAttachment = async attachmentId => {
    return api.delete(`/attachments/${attachmentId}`);
  };

  const handleRemoveAttachment = async attachment => {
    // Upload em andamento
    if (attachment.status === 'loading') {
      attachment.controller?.abort();

      setAttachments(prev => prev.filter(item => item.tempId !== attachment.tempId));

      return;
    }

    // Upload com erro
    if (attachment.status === 'error') {
      setAttachments(prev => prev.filter(item => item.tempId !== attachment.tempId));

      return;
    }

    // Upload concluído
    if (attachment.status === 'success') {
      try {
        await deleteAttachment(attachment.id);

        setAttachments(prev => prev.filter(item => item.tempId !== attachment.tempId));
      } catch (error) {
        toast.error('Erro ao remover arquivo');
      }
    }
  };

  const hasInvalidVideoLink = text => {
    const urlRegex = /(https?:\/\/[^\s]+)/gi;

    const urls = text.match(urlRegex);

    if (!urls?.length) {
      return false;
    }

    const allowedHosts = ['youtube.com', 'www.youtube.com', 'youtu.be', 'vimeo.com', 'www.vimeo.com'];

    return urls.some(url => {
      try {
        const hostname = new URL(url).hostname.toLowerCase();

        return !allowedHosts.includes(hostname);
      } catch {
        return true;
      }
    });
  };

  const getLimitMessage = () => {
    if (!isStudent) return;

    const sendMessageLimit = meta?.['daily-messages-limit']; // Limite de mensagens por dia
    const messagesSentToday = meta?.['daily-messages-count']; // Quantidade de envios

    if (messagesSentToday >= sendMessageLimit) {
      return (
        <p className="w-full text-xs text-[#FF726C]">
          Hoje <b>você já enviou todas as suas mensagens para esta aula </b>.
        </p>
      );
    } else if (messagesSentToday > 0) {
      return (
        <p className="w-full text-xs text-[#EE8445]">
          Faltam <b>{sendMessageLimit - messagesSentToday}</b> mensagens para o limite de hoje nesta aula ({messagesSentToday}/{sendMessageLimit}).
        </p>
      );
    } else {
      return (
        <p className="w-full text-xs text-success">
          Nesta aula você pode enviar <b>até {sendMessageLimit} mensagens por dia</b>. O limite é renovado diariamente.
        </p>
      );
    }
  };

  const showLinkWarning = hasInvalidVideoLink(commentForm.values.body);

  // Se o usuário colocar apenas o link do vídeo sem comentário, o envio do link do vídeo é feito
  useEffect(() => {
    if (hasVideoLink && commentForm.values.body.trim() === videoLink) {
      handleSubmitFile();
    }
    if (commentForm?.values?.file) {
      handleSubmitFile();
    }
  }, [commentForm.values.body, commentForm.values.file]);

  const sentAllMessages = isStudent && meta && meta['daily-messages-count'] >= meta['daily-messages-limit'];

  return (
    <ChatFooter variant="reply">
      <ChatForm onSubmit={handleSubmit}>
        <Input
          className="!w-10 grow"
          type="text"
          name="body"
          id="body"
          placeholder={t('activity.placeholderWriteComment')}
          aria-label={t('activity.placeholderWriteComment')}
          value={commentForm.values.body}
          onChange={commentForm.handleChange}
          data-tooltip-id="video-link-tooltip"
          disabled={sentAllMessages || isSubmittingFile}
        />

        {showLinkWarning && (
          <Tooltip
            id="video-link-tooltip"
            isOpen
            place="top"
            opacity={1}
            className="tooltip flex max-w-[320px] items-center gap-1.5"
            border="1px solid var(--color-neutral-lightest)"
            style={{
              backgroundColor: 'var(--color-neutral-bright)',
              color: 'var(--color-neutral-darker)'
            }}
          >
            <img
              src={warning}
              alt="Alerta"
              className="h-3 w-3"
            />{' '}
            Atenção! Você só pode enviar links de vídeo do Youtube ou Vimeo
          </Tooltip>
        )}

        <FiPaperclip
          className={`shrink-0 text-lg text-primary ${sentAllMessages ? 'opacity-50' : 'cursor-pointer'}`}
          onClick={() => {
            if (sentAllMessages) return;
            fileInputRef.current?.click();
          }}
          data-tooltip-id="attach-file-tooltip"
        />
        <Tooltip
          id="attach-file-tooltip"
          className="tooltip"
          opacity={1}
          border="1px solid var(--color-neutral-lightest)"
          style={{ backgroundColor: 'var(--color-neutral-bright)', color: 'var(--color-neutral-darker)' }}
        >
          Anexar arquivo
        </Tooltip>

        <Input
          type="file"
          name="file"
          id="file"
          className="hidden"
          ref={fileInputRef}
          accept="audio/*, image/*, application/pdf, .doc, .docx, .txt, .xls, .xlsx, .csv"
          onChange={e => {
            const file = e.target.files[0];

            const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

            if (file.size > MAX_FILE_SIZE) {
              toast.error('O arquivo deve ter no máximo 10 MB.');
              e.target.value = '';
              return;
            }

            if (file) {
              commentForm.setFieldValue('file', file);
            }
          }}
        />

        <Button
          size="icon"
          type="submit"
          disabled={loading || sentAllMessages}
          className="shrink-0"
        >
          <FiSend />
        </Button>
      </ChatForm>

      {getLimitMessage()}

      {attachments.length > 0 && (
        <div className="mt-2 flex flex-col gap-2 overflow-y-auto">
          {attachments.map(file => (
            <div
              key={file.tempId}
              className="flex items-center justify-between gap-2 rounded border border-neutral-lightest bg-white p-1"
            >
              <div className="flex w-full items-center justify-between gap-1 overflow-hidden text-sm text-neutral">
                <div className={clsx('flex items-center gap-1.5', file.status === 'error' ? 'max-w-[70%] md:max-w-[55%]' : 'max-w-[85%]')}>
                  {file.status === 'loading' && (
                    <LoaderSpinner
                      text=""
                      className="w-6"
                    />
                  )}

                  <span className={`overflow-hidden text-ellipsis text-nowrap ${file.status === 'error' ? 'text-danger' : file?.status === 'success' ? 'text-success' : ''}`}>{file.name}</span>
                </div>

                {file.status === 'loading' && <span>Enviando...</span>}

                {file.status === 'error' && <span className="text-nowrap text-danger">Falha no upload</span>}
              </div>

              <CardIcon>
                <FiX
                  className="cursor-pointer text-lg text-primary"
                  onClick={() => handleRemoveAttachment(file)}
                />
              </CardIcon>
            </div>
          ))}
        </div>
      )}
    </ChatFooter>
  );
}
